/**
 * Caption entrance-animation math (phase 2) — pure helpers shared by the
 * composition and tests. Everything is a deterministic function of the
 * cue-relative time, so preview and render agree by construction.
 */
import { splitEmphasis } from "./srt.ts";

export type CaptionAnimIn = "fade" | "slide-up" | "pop" | "typewriter" | "word-stagger";
export type CaptionAnimOut = "fade" | "slide-down" | "pop";

export const DEFAULT_ANIM_MS = 300;

/** Exit window may never eat more than this share of the cue. The entrance
 *  clamps to 0.6, so 0.4 guarantees the two windows can never overlap. */
export const OUT_MAX_CUE_FRACTION = 0.4;

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** Cubic ease-out — snappy caption entrances without a spring dependency. */
export function easeOut(p: number): number {
  const c = clamp01(p);
  return 1 - (1 - c) ** 3;
}

/** Cubic ease-in — exits accelerate away (mirror of easeOut). */
export function easeIn(p: number): number {
  return clamp01(p) ** 3;
}

/**
 * Block-level style for the whole caption block. typewriter/word-stagger
 * return {} — their effect is content-level (see clipLineToVisibleChars /
 * the word renderer), not a block transform.
 */
export function animBlockStyle(
  anim: CaptionAnimIn,
  progress: number,
  fontSizePx: number,
): { opacity?: number; transform?: string } {
  const p = clamp01(progress);
  if (p >= 1) return {};
  switch (anim) {
    case "fade":
      return { opacity: easeOut(p) };
    case "slide-up": {
      const rise = 0.6 * fontSizePx;
      return { opacity: easeOut(p), transform: `translateY(${(1 - easeOut(p)) * rise}px)` };
    }
    case "pop": {
      // Slight overshoot: 0.9 → 1.04 → 1.0.
      const s = p < 0.7 ? 0.9 + (0.14 * easeOut(p / 0.7)) : 1.04 - 0.04 * ((p - 0.7) / 0.3);
      return { opacity: easeOut(p * 2), transform: `scale(${s.toFixed(4)})` };
    }
    default:
      return {};
  }
}

/**
 * Exit-window length for a cue: the requested duration clamped to the tail
 * fraction. With the entrance clamped to the first 60% and this to the last
 * 40%, the two windows can never co-fire on any cue length.
 */
export function exitWindowMs(cueDurMs: number, outDurationMs: number | undefined): number {
  const want = outDurationMs ?? DEFAULT_ANIM_MS;
  return cueDurMs > 0 ? Math.min(want, cueDurMs * OUT_MAX_CUE_FRACTION) : want;
}

/** 0 → 1 across the exit window (1 = cue end); 0 before the window opens. */
export function exitProgress(tMs: number, cueDurMs: number, outMs: number): number {
  if (outMs <= 0) return 0;
  return clamp01((tMs - (cueDurMs - outMs)) / outMs);
}

/**
 * Block-level style for the caption's EXIT. `progress` runs 0 → 1 across the
 * exit window (1 = cue end). progress ≤ 0 → {} (inert — the founding path
 * for every frame before the window opens). All outs are block-level; there
 * is deliberately no typewriter/stagger exit (content-level outs would fight
 * karaoke indices). Every out keeps SOME visibility until p=1 — a caption
 * that blanks while speech continues reads as a sync bug.
 */
export function outBlockStyle(
  anim: CaptionAnimOut,
  progress: number,
  fontSizePx: number,
): { opacity?: number; transform?: string } {
  if (progress <= 0) return {};
  const p = clamp01(progress);
  switch (anim) {
    case "fade":
      return { opacity: 1 - easeIn(p) };
    case "slide-down": {
      const drop = 0.6 * fontSizePx;
      return { opacity: 1 - easeIn(p), transform: `translateY(${(easeIn(p) * drop).toFixed(2)}px)` };
    }
    case "pop": {
      // Inverse of the entrance pop: shrink slightly while fading. Opacity
      // reaches 0 exactly at p=1 (a ×1.5 speed-up here left the caption
      // invisible for the window's last third — critic-caught).
      const s = 1 - 0.1 * easeIn(p);
      return { opacity: 1 - easeIn(p), transform: `scale(${s.toFixed(4)})` };
    }
    default:
      return {};
  }
}

/** Grapheme-cluster split — an Arabic base letter + its tashkeel must
 *  reveal on the SAME frame (critic-caught: code-point slicing popped
 *  diacritics onto already-drawn letters). Falls back to code points
 *  where Intl.Segmenter is missing (identical everywhere we run). */
const SEGMENTER: Intl.Segmenter | null =
  typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter() : null;

function graphemes(text: string): string[] {
  if (!SEGMENTER) return [...text];
  return [...SEGMENTER.segment(text)].map((s) => s.segment);
}

/** Visible (tag-free) grapheme count of a line. */
export function visibleLength(line: string): number {
  return splitEmphasis(line).reduce((n, s) => n + graphemes(s.text).length, 0);
}

/**
 * Typewriter clip: the first `budget` VISIBLE graphemes of `line`,
 * preserving `<em>` spans (a span cut mid-way closes cleanly). budget ≤ 0
 * → ""; budget ≥ visibleLength → the line unchanged.
 */
export function clipLineToVisibleChars(line: string, budget: number): string {
  if (budget <= 0) return "";
  let remaining = budget;
  let out = "";
  for (const seg of splitEmphasis(line)) {
    if (remaining <= 0) break;
    const chars = graphemes(seg.text);
    const take = chars.slice(0, remaining).join("");
    remaining -= chars.length;
    // Re-attach an explicit per-span color so a colored word keeps its color
    // through the typewriter reveal (critic-caught: it painted gold until the
    // reveal finished, then snapped to the real color).
    out += seg.emphasized ? `<em${seg.color ? ` c="${seg.color}"` : ""}>${take}</em>` : take;
  }
  return out;
}
